import React, {useEffect, useState} from "react";
import {formatDestinationAccountType, formatCurrency, formatDate, formatSortCode, formatTime} from "../../utils/helpers";
import Select from "../Form/Select/Select";
import Button from "../Form/Button/Button";
import ConfirmInstructionButton from "./ConfirmInstructionButton";
import {CurrentTermDetailsBox} from "./MaturityDetailsBoxes";
import {constructDateTime} from "./MaturityFlowHelperFunctions";

/** @import {OtherAccount, PayawayDetailsConfirmationProps} from './PayawayDetailsConfirmationTypes' */
/** @import {Instruction} from "./MaturityOptionsTypes" */

import warningIcon from "../../assets/images/warning-outline.svg";
import WarningMessage from "./PartialRenewal/PartialRenewalForm/WarningMessage/WarningMessage";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

function PayawayDetailsConfirmation(/** @type {PayawayDetailsConfirmationProps} */ {
    accountList,
    maturingAccount,
    selectedAccount,
    setSelectedAccount,
    customerMobileNumber,
    onStepComplete,
    onStepCancel,
    multipleMaturingAccounts,
    featureEnabled
}) {

    const [displayDropdown] = useState(accountList ? accountList.length > 1 : false);
    const [selectedPayawayDestinationAccount, setSelectedPaywayDestinationAccount] = useState(/** @type {OtherAccount | null | undefined} */ (null));
    const [instruction, setInstruction] = useState(/** @type {Instruction | null} */ (null));

    const analytics = useUnifiedAnalytics();

    useEffect(()=> {
        // analytics.trackAnalyticsPageEvent(
        //     'generic load',
        //     {},
        //     '',
        //     'page|load|generic'
        // );

    },[])

    useEffect( () => {
        setSelectedAccount(accountList && accountList.length > 0 ? accountList[0] : undefined)
    },[displayDropdown])

    useEffect(()=>{
        setInstructionOnLoad(maturingAccount?.maturity?.maturityInstruction,
            Boolean(accountList && accountList.filter(a => a.product.type !== 'linked').length > 0))
    },[maturingAccount])

    const setInstructionOnLoad = (/** @type {string | undefined} */ instruction, /** @type {boolean} */ multipleAccounts) => {
        if (instruction && instruction.startsWith("Payaway")) {
            //@ts-expect-error sometimes this is a string and sometimes it is an object
            setInstruction(instruction);
            setSelectedPaywayDestinationAccount(selectedAccount);
        }
        if (!multipleAccounts) {
            setInstruction({ name: "PayawayLinked" });
            setSelectedPaywayDestinationAccount(selectedAccount);
        }
    }

    const renderSelectedDestinationAccountDetails = (/** @type {OtherAccount} */ payawayDestinationAccount) => {

        if (payawayDestinationAccount.product.name === "Linked Account") {
            return (
                <>
                    <div className="maturity3__narrow-box maturity3__emphasis-box maturity3__emphasis-box--blue maturity3__emphasis-box--narrow maturity3__grid">
                        <h1>Your linked account details:</h1>
                        <div></div>
                        <div>Sort code:</div>
                        <div><b>{formatSortCode(payawayDestinationAccount.sortCode)}</b></div>
                        <div>Account number:</div>
                        <div><b>{payawayDestinationAccount.accountNumber}</b></div>
                    </div>

                    {renderWarningMessage(payawayDestinationAccount)}
                </>
            );
        } else {
            return (
                <>
                    <div
                        className="maturity3__narrow-box maturity3__emphasis-box maturity3__emphasis-box--blue maturity3__emphasis-box--narrow maturity3__grid">
                        <div>Account number:</div>
                        <div>
                            <b>{payawayDestinationAccount.accountNumber}</b>
                        </div>
                        <div>Balance:</div>
                        <div>
                            <b>{formatCurrency(payawayDestinationAccount.balance)}</b>
                        </div>
                    </div>

                    { renderWarningMessage(payawayDestinationAccount) }
                </>
            );
        }
    };
    const renderDestinationAccountDropDown = (/** @type {OtherAccount[]} */ accountList) => {
        const mapAccountDropDown = (/** @type {OtherAccount[]} */ accountList) => {
            return accountList.map((account) => {
                return {
                    "label": `${account.product.name} - ${account.accountNumber}`,
                    "value": account.accountNumber
                };
            });
        };
        const onSelect = (/** @type {string} */ selectedAccountNumber) => {
            const account = accountList.find(a => a.accountNumber === selectedAccountNumber);
            if (account) {
                setSelectedAccount(account);

                setSelectedPaywayDestinationAccount(account);

                if (account.product.name === "Linked Account") {
                    setInstruction({ name: "PayawayLinked" });
                } else {
                    setInstruction({ name: "PayawayOtherAccount" });
                }
            }
        };

        return <>
            <Select
                name="account"
                placeholder="Select account"
                options={mapAccountDropDown(accountList)}
                onChange={option => option && onSelect(option.value)}
                disabled={false}
                //@ts-expect-error not sure how to handle this from a typescript perspective
                value={selectedPayawayDestinationAccount ? mapAccountDropDown([selectedPayawayDestinationAccount]) : null}
            />
            {selectedPayawayDestinationAccount && renderSelectedDestinationAccountDetails(selectedPayawayDestinationAccount)}
        </>;
    };

    const payawayAnalytics = (/** @type {string | undefined} */ product) => {
        analytics.trackAnalyticsClickTransactionEvent(
            'confirm',
            maturingAccount?.product?.code.split('_')[0] || '',
            `transfer everything-${selectedPayawayDestinationAccount?.product.name || 'linked'}`,
            selectedPayawayDestinationAccount?.product.name || 'linked',
            "maturity-instruction|click|confirm-instruction",
            'maturity',
            maturingAccount?.product?.code
        );
    }

    const onClickBack = () => {
         analytics.trackAnalyticsClickEvent(
            "back",
            `/maturity-instruction/${maturingAccount?.id}`,
            "component right",
            maturingAccount?.product.code || '',
            "maturity",
            {},
            "link click",
            "navigation|back|maturity",
            "maturity"
        );


        onStepCancel()
    }
    const renderWarningMessage = (/** @type {OtherAccount} */ payawayDestinationAccount) => {
        const isLinkedAccount = payawayDestinationAccount.product.name === "Linked Account";
        const accountType = formatDestinationAccountType(payawayDestinationAccount?.product.type);
        const maturityDate = maturingAccount?.maturity ? formatDate(maturingAccount?.maturity?.maturityDate) : '';
        const maxWithdrawalLimit = formatCurrency(payawayDestinationAccount?.product?.maxWithdrawalLimit, false);

        const warningBody = isLinkedAccount
            ? "Please make sure your linked account is still active, and that we have the right details. If you need to update it, go to Manage account(s) to do so before you give us any instructions."
            : `Please note: if your ${accountType} is closed on ${maturityDate}, or if this payment would take the balance over ${maxWithdrawalLimit}, we’ll pay the whole amount into your linked account instead.`;

        return (
            <WarningMessage
                body={warningBody}
                selectedAccountType={payawayDestinationAccount?.product?.name}
            />
        );
    };

    return <>
        <h1 className="text__title">Transfer everything</h1>
        <CurrentTermDetailsBox maturingAccount={maturingAccount}/>
        {displayDropdown ?
            <>
                <p>Please select which account you’d like us to transfer your funds to:</p>
                {accountList && renderDestinationAccountDropDown(accountList)}

            </> : <>
                <p>Have your savings and interest transferred to your linked account and close your Fixed Rate
                    Saver.</p>
                {accountList && renderSelectedDestinationAccountDetails(accountList[0])}
            </>
        }

        <p>You can change your instruction in your online dashboard at any time
            before <b>{formatTime(constructDateTime(new Date(), maturingAccount?.maturity?.maturityWindowCloseTime))}</b> on {formatDate(maturingAccount?.maturity?.maturityWindowCloseDate)}.</p>
        <div className={"maturity3__flex-row"} style={{justifyContent: "normal"}}>
            <Button size='med' customClassNames={["navigation-button__back"]} label={"Back"} onClick={onClickBack}/>
            <ConfirmInstructionButton
                account={maturingAccount}
                instruction={instruction}
                destinationAccount={selectedPayawayDestinationAccount}
                mobileNumber={customerMobileNumber}
                disabled={!instruction}
                onOTPSuccess={() => onStepComplete(instruction)}
                showConfirmationOnSuccess={true}
                customAnalytics={() => payawayAnalytics(maturingAccount?.product.code)}
                multipleMaturingAccounts={multipleMaturingAccounts}/>
        </div>
    </>;
};

export default PayawayDetailsConfirmation;